import { CalendarDate, DateValue, parseDate } from '@internationalized/date'
import React, { DOMAttributes, useEffect, useState } from 'react'
import { CalendarProps, useCalendar, useLocale } from 'react-aria'
import { useCalendarState } from 'react-stately'
import { Box } from '../../../../core-components'
import { Events } from '../../../../types'
import { OmitInternalProperties } from '../../../types'
import {
  ActiveView,
  BaseCalendar,
  createCalendar,
  useSyncVisibleDate,
} from '../../base-calendar/BaseCalendar'

type Props = CalendarProps<DateValue> & {
  INTERNAL_defaultActiveView?: ActiveView
  onSelectKeyDown?: DOMAttributes<HTMLButtonElement>['onKeyDown']
  variant?: 'inline' | 'popover'
  eventsRef?: React.RefObject<Events<'focus'> | null | undefined>
}

export const DatePickerCalendarInternal = ({
  onSelectKeyDown,
  INTERNAL_defaultActiveView,
  variant = 'popover',
  eventsRef,
  ...props
}: Props) => {
  const date = props.value as CalendarDate | null
  const { locale } = useLocale()
  const [visibleDate, setVisibleDate] = useState(date)
  const state = useCalendarState({
    ...props,
    locale,
    createCalendar,
    focusedValue: visibleDate,
    onFocusChange: setVisibleDate,
  })

  const setFocused = state.setFocused
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined
    if (eventsRef) {
      eventsRef.current = {
        focus: () => {
          timeout = setTimeout(() => setFocused(true), 0)
        },
      }
      return () => {
        if (timeout) {
          clearTimeout(timeout)
        }
        eventsRef.current = undefined
      }
    }
  }, [eventsRef, setFocused])

  useSyncVisibleDate(date, setVisibleDate)

  const { calendarProps, prevButtonProps, nextButtonProps } = useCalendar(props, state)

  if (variant === 'inline') {
    return (
      <Box {...calendarProps} overflow='hidden' position='relative'>
        <BaseCalendar
          onSelectKeyDown={onSelectKeyDown}
          state={state}
          prevButtonProps={prevButtonProps}
          nextButtonProps={nextButtonProps}
          INTERNAL_defaultActiveView={INTERNAL_defaultActiveView}
          onVisibleDateChange={setVisibleDate}
        />
      </Box>
    )
  }

  return (
    <Box {...calendarProps} w='312px' boxShadow='border/popover' borderRadius={12}>
      <Box bg='bg/dropdown' borderRadius={12} overflow='hidden' position='relative'>
        <BaseCalendar
          onSelectKeyDown={onSelectKeyDown}
          state={state}
          prevButtonProps={prevButtonProps}
          nextButtonProps={nextButtonProps}
          INTERNAL_defaultActiveView={INTERNAL_defaultActiveView}
          onVisibleDateChange={setVisibleDate}
        />
      </Box>
    </Box>
  )
}

interface WrapperProps {
  INTERNAL_defaultActiveView?: ActiveView
  onChange: (value: string) => void
  value?: string
  variant?: 'inline' | 'popover'
  eventsRef?: React.RefObject<Events<'focus'> | null | undefined>
}

export const DatePickerCalendarWrapper = ({
  value,
  onChange,
  variant,
  eventsRef,
  INTERNAL_defaultActiveView,
}: WrapperProps) => {
  return (
    <DatePickerCalendarInternal
      INTERNAL_defaultActiveView={INTERNAL_defaultActiveView}
      variant={variant}
      value={value ? parseDate(value) : (null as unknown as undefined)}
      onChange={date => onChange(date.toString())}
      eventsRef={eventsRef}
    />
  )
}

export const DatePickerCalendar = (props: OmitInternalProperties<WrapperProps>) => (
  <DatePickerCalendarWrapper {...props} />
)
